import { useState, useRef } from "react";
import { Play, Pause, Loader2, User, UserRound } from "lucide-react";

/**
 * Selezione voce 24Vox prima della telefonata.
 * Due card grandi: Maschile / Femminile. Ogni card ha un pulsante "Ascolta" che
 * scarica una preview MP3 generata dal backend (cache lato server).
 * Al click sulla card, viene chiamato onSelect("M"|"F") e parte la chiamata.
 */
export default function VoiceGenderSelector({ onSelect }) {
  const [loadingPreview, setLoadingPreview] = useState(null); // "M" | "F" | null
  const [playing, setPlaying] = useState(null); // "M" | "F" | null
  const audioRef = useRef(null);
  const cacheRef = useRef({}); // base64 cache lato client

  const stop = () => {
    try {
      audioRef.current?.pause();
    } catch {}
    audioRef.current = null;
    setPlaying(null);
  };

  const playPreview = async (gender) => {
    if (playing === gender) {
      stop();
      return;
    }
    stop();
    setLoadingPreview(gender);
    try {
      let b64 = cacheRef.current[gender];
      if (!b64) {
        const backend = process.env.REACT_APP_BACKEND_URL;
        const resp = await fetch(`${backend}/api/voice-preview/${gender}`);
        if (!resp.ok) throw new Error("preview failed");
        const data = await resp.json();
        b64 = data.audio_b64;
        cacheRef.current[gender] = b64;
      }
      const audio = new Audio(`data:audio/mp3;base64,${b64}`);
      audioRef.current = audio;
      audio.onended = () => setPlaying(null);
      audio.onerror = () => setPlaying(null);
      await audio.play();
      setPlaying(gender);
    } catch (e) {
      console.error("preview error", e);
    } finally {
      setLoadingPreview(null);
    }
  };

  const cards = [
    {
      gender: "M",
      label: "Maschile",
      Icon: User,
      gradient: "from-brand-olive/10 to-brand-olive/0",
      ring: "ring-brand-olive/30",
      ringHover: "hover:ring-brand-olive/60",
      accent: "text-brand-olive",
    },
    {
      gender: "F",
      label: "Femminile",
      Icon: UserRound,
      gradient: "from-brand-terra/10 to-brand-terra/0",
      ring: "ring-brand-terra/30",
      ringHover: "hover:ring-brand-terra/60",
      accent: "text-brand-terra",
    },
  ];

  return (
    <div className="w-full" data-testid="voice-gender-selector">
      <p className="text-xs font-medium tracking-[0.25em] text-brand-inkSoft uppercase text-center mb-3">
        Scegli la voce della tua demo
      </p>
      <p className="text-sm text-brand-inkSoft text-center mb-8">
        Tocca per ascoltare un campione, poi seleziona quale userà a rispondere.
      </p>

      <div className="grid grid-cols-2 gap-4 sm:gap-6 max-w-md mx-auto">
        {cards.map(({ gender, label, Icon, gradient, ring, ringHover, accent }) => {
          const isLoading = loadingPreview === gender;
          const isPlaying = playing === gender;
          return (
            <div
              key={gender}
              className={`group relative bg-gradient-to-b ${gradient} bg-white border border-brand-line rounded-2xl p-5 sm:p-6 ring-2 ${ring} ${ringHover} transition-all`}
              data-testid={`voice-card-${gender.toLowerCase()}`}
            >
              {/* Icona + label */}
              <button
                type="button"
                onClick={() => onSelect(gender)}
                className="block w-full text-center mb-4"
                data-testid={`voice-select-${gender.toLowerCase()}`}
                aria-label={`Seleziona voce ${label} e avvia la chiamata`}
              >
                <div className={`w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-3 rounded-full bg-brand-bg2 flex items-center justify-center ${accent}`}>
                  <Icon className="w-8 h-8 sm:w-10 sm:h-10" strokeWidth={1.5} />
                </div>
                <div className="font-heading text-lg sm:text-xl text-brand-ink">{label}</div>
              </button>

              {/* Bottone ascolta */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  playPreview(gender);
                }}
                className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-brand-bg2 hover:bg-brand-line/50 text-xs font-medium ${accent} transition`}
                data-testid={`voice-preview-${gender.toLowerCase()}`}
              >
                {isLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-3.5 h-3.5" />
                ) : (
                  <Play className="w-3.5 h-3.5" />
                )}
                {isLoading ? "carico..." : isPlaying ? "stop" : "ascolta"}
              </button>

              {/* CTA "seleziona" più esplicita sotto */}
              <button
                type="button"
                onClick={() => onSelect(gender)}
                className={`mt-3 w-full text-xs font-semibold uppercase tracking-wider ${accent} hover:underline`}
                data-testid={`voice-confirm-${gender.toLowerCase()}`}
              >
                Scegli questa →
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
